<template>
  <div class="external-auth-page">
    <div class="auth-container">
      <div class="auth-card">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loader-wrapper">
            <svg class="spinner" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="28" stroke="#94bd23" stroke-width="6" stroke-linecap="round" stroke-dasharray="175" stroke-dashoffset="87.5"/>
            </svg>
          </div>
          <h2 class="loading-title">Autenticando...</h2>
          <p class="loading-message">Por favor, aguarde enquanto validamos suas credenciais.</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#FEE2E2"/>
              <path d="M32 20v16M32 44v.5" stroke="#EF4444" stroke-width="4" stroke-linecap="round"/>
            </svg>
          </div>
          <h2 class="error-title">Falha na Autenticação</h2>
          <p class="error-message">{{ errorMessage }}</p>
          <button @click="redirectToLogin" class="btn-primary">
            Voltar ao Login
          </button>
        </div>

        <!-- Success State -->
        <div v-else-if="success" class="success-state">
          <div class="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#D1FAE5"/>
              <path d="M20 32l8 8 16-16" stroke="#10B981" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h2 class="success-title">Autenticação Realizada!</h2>
          <p class="success-message">Redirecionando para o dashboard...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ExternalAuth',
  data() {
    return {
      loading: true,
      error: false,
      success: false,
      errorMessage: ''
    };
  },
  mounted() {
    this.handleExternalAuth();
  },
  methods: {
    ...mapActions({
      externalSignIn: 'auth/externalSignIn',
    }),
    
    async handleExternalAuth() {
      try {
        // Obtém o token da query string
        const token = this.$route.query.token;
        
        if (!token) {
          this.showError('Token de autenticação não fornecido.');
          return;
        }

        // Chama a action de autenticação externa
        const response = await this.externalSignIn(token);
        
        if (response.status === 200 && response.data.success) {
          // Autenticação bem-sucedida
          this.loading = false;
          this.success = true;
          
          // Redireciona para o dashboard após 1.5 segundos
          setTimeout(() => {
            this.$router.replace({ path: '/' });
          }, 1500);
        } else {
          this.showError(response.data.message || 'Erro ao processar autenticação.');
        }
      } catch (error) {
        console.error('Erro na autenticação externa:', error);
        
        let message = 'Erro interno ao processar autenticação.';
        
        if (error.response) {
          if (error.response.data?.message) {
            message = error.response.data.message;
          } else if (error.response.status === 401) {
            message = 'Token inválido ou expirado.';
          } else if (error.response.status === 400) {
            message = 'Requisição inválida.';
          }
        } else if (error.request) {
          message = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
        }
        
        this.showError(message);
      }
    },
    
    showError(message) {
      this.loading = false;
      this.error = true;
      this.errorMessage = message;
    },
    
    redirectToLogin() {
      this.$router.push({ path: '/login' });
    }
  }
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.external-auth-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #00235C 0%, #001a45 100%);
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
    top: -300px;
    left: -300px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 50%;
    bottom: -250px;
    right: -250px;
  }
}

.auth-container {
  z-index: 1;
  padding: 2rem;
}

.auth-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 3rem;
  min-width: 400px;
  max-width: 500px;
  animation: slideUp 0.6s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 2rem 0;
}

.loader-wrapper {
  margin-bottom: 2rem;
  display: inline-block;
}

.spinner {
  animation: rotate 1.5s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

.loading-message {
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 2rem 0;
}

.error-icon {
  margin-bottom: 2rem;
  display: inline-block;
  animation: scaleIn 0.6s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.error-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

.error-message {
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.btn-primary {
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #94bd23 0%, #7da51e 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(148, 189, 35, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}

/* Success State */
.success-state {
  text-align: center;
  padding: 2rem 0;
}

.success-icon {
  margin-bottom: 2rem;
  display: inline-block;
  animation: scaleIn 0.6s ease;
}

.success-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

.success-message {
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Responsividade */
@media (max-width: 640px) {
  .auth-card {
    min-width: 0;
    width: 100%;
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }

  .loading-title,
  .error-title,
  .success-title {
    font-size: 1.5rem;
  }

  .loading-message,
  .error-message,
  .success-message {
    font-size: 0.875rem;
  }
}
</style>
